import type { ApiErrorCode } from '@wr/shared';

/**
 * Calls to the API, on the same origin (App Platform routes /api to it).
 * The session cookie is HttpOnly: the app never sees it. Every change sends
 * the CSRF token of the session as a header.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | 'offline',
    readonly body: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

let csrfToken: string | undefined;

export function setCsrfToken(token: string | undefined): void {
  csrfToken = token;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== 'GET') headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  // The person's time zone, so security emails show their local time.
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timeZone) headers['x-time-zone'] = timeZone;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiError(0, 'offline');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(res.status, (data.error as ApiErrorCode | undefined) ?? 'internal', data);
  }
  const token =
    (data.csrfToken as string | undefined) ??
    ((data.session as { csrfToken?: string } | undefined)?.csrfToken as string | undefined);
  if (token) csrfToken = token;
  return data as T;
}

/** Sends a file as it is (a scanned card, a receipt), with the session's CSRF token. */
export async function putFile<T>(
  path: string,
  file: Blob,
  method: 'PUT' | 'POST' = 'PUT',
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': file.type || 'image/jpeg' };
  if (csrfToken) headers['x-csrf-token'] = csrfToken;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: file,
    });
  } catch {
    throw new ApiError(0, 'offline');
  }
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(res.status, (data.error as ApiErrorCode | undefined) ?? 'internal', data);
  }
  return data as T;
}

/** Sends a large file (a PDF of the library) and reports how much went, from 0 to 1. */
export function uploadFile<T>(
  path: string,
  file: Blob,
  onProgress: (sent: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `/api${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
    if (csrfToken) xhr.setRequestHeader('x-csrf-token', csrfToken);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'offline'));
    xhr.onload = () => {
      let data: Record<string, unknown> = {};
      try {
        data = JSON.parse(xhr.responseText) as Record<string, unknown>;
      } catch {
        // An empty answer.
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else {
        const code = (data.error as ApiErrorCode | undefined) ?? 'internal';
        reject(new ApiError(xhr.status, code, data));
      }
    };
    xhr.send(file);
  });
}

/** Downloads a file the API builds (a PDF): saved by the browser under the name the API gives. */
export async function downloadFile(path: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'offline');
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(res.status, (data.error as ApiErrorCode | undefined) ?? 'internal', data);
  }
  const name =
    /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'download';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body);
export const patch = <T>(path: string, body?: unknown) => api<T>('PATCH', path, body);
export const del = <T>(path: string) => api<T>('DELETE', path);
export const put = <T>(path: string, body?: unknown) => api<T>('PUT', path, body);

/** A sentence for the person, for the errors every screen may meet. */
export function errorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong. Try again.';
  switch (error.code) {
    case 'offline':
      return 'No connection. This needs the network: try again when you are online.';
    case 'rate_limited':
      return 'Too many attempts from this network. Wait a few minutes, then try again.';
    case 'forbidden':
      return 'You are not allowed to do this.';
    case 'unauthenticated':
      return 'Your session has ended. Sign in again.';
    case 'invalid_token':
      return 'This link is no longer valid: it was already used, it expired, or a newer one was sent.';
    case 'csrf':
      return 'This page is out of date. Reload it, then try again.';
    case 'unavailable':
      return 'HubSpot is not answering. Try again in a moment.';
    default:
      return 'Something went wrong. Try again.';
  }
}
